import { useState } from "react";
import { toast } from "sonner";
import api, { naira } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/store/Reveal";

const STATUS_STEPS = ["Placed", "Confirmed", "Delivered"];

export default function TrackOrder() {
  const [orderNumber, setOrderNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);

  const track = async (e) => {
    e.preventDefault();
    setLoading(true);
    setOrder(null);
    try {
      const { data } = await api.post("/orders/track", { orderNumber, phone });
      setOrder(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Order not found");
    } finally {
      setLoading(false);
    }
  };

  const currentStep = order ? STATUS_STEPS.indexOf(order.status) : -1;

  return (
    <div className="pt-28 md:pt-36 pb-24 min-h-screen" data-testid="track-page">
      <div className="max-w-xl mx-auto px-5">
        <Reveal>
          <p className="overline text-terracotta mb-3">Order tracking</p>
          <h1 className="font-serif text-5xl md:text-6xl text-espresso tracking-tight mb-8">Track your order</h1>
        </Reveal>

        <form onSubmit={track} className="bg-sand rounded-2xl p-6 space-y-4">
          <div>
            <label className="text-sm text-bark mb-1.5 block">Order number</label>
            <Input
              required
              data-testid="track-order-number"
              placeholder="17B-XXXXXX"
              value={orderNumber}
              onChange={(e) => setOrderNumber(e.target.value)}
              className="rounded-xl border-sandline bg-bone focus-visible:ring-terracotta"
            />
          </div>
          <div>
            <label className="text-sm text-bark mb-1.5 block">Phone number</label>
            <Input
              required
              data-testid="track-phone"
              placeholder="Phone used at checkout"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="rounded-xl border-sandline bg-bone focus-visible:ring-terracotta"
            />
          </div>
          <Button type="submit" disabled={loading} data-testid="track-submit" className="w-full rounded-full bg-terracotta text-bone hover:bg-espresso h-12">
            {loading ? "Searching…" : "Track order"}
          </Button>
        </form>

        {order && (
          <div className="mt-8 bg-bone border border-sandline rounded-2xl p-6" data-testid="track-result">
            <div className="flex justify-between items-center mb-6">
              <div>
                <p className="overline text-bark">Order</p>
                <p className="font-serif text-2xl text-espresso">#{order.orderNumber}</p>
              </div>
              <span className="text-espresso font-semibold">{naira(order.total)}</span>
            </div>

            {order.status === "Cancelled" ? (
              <p className="text-destructive font-medium" data-testid="track-status">This order was cancelled.</p>
            ) : (
              <div className="flex justify-between relative mb-2" data-testid="track-status">
                {STATUS_STEPS.map((s, i) => (
                  <div key={s} className="flex-1 flex flex-col items-center relative z-10">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${i <= currentStep ? "bg-terracotta text-bone" : "bg-sand text-bark"}`}>
                      {i + 1}
                    </div>
                    <span className={`text-xs mt-2 ${i <= currentStep ? "text-espresso" : "text-bark"}`}>{s}</span>
                  </div>
                ))}
                <div className="absolute top-4 left-8 right-8 h-0.5 bg-sand -z-0">
                  <div className="h-full bg-terracotta transition-all" style={{ width: `${(Math.max(0, currentStep) / (STATUS_STEPS.length - 1)) * 100}%` }} />
                </div>
              </div>
            )}

            <div className="mt-6 border-t border-sandline pt-4 space-y-2">
              {order.items?.map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-bark">{it.name} × {it.quantity}</span>
                  <span className="text-espresso">{naira(it.price * it.quantity)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
